import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ServerError } from './api';
import { fa, toLatin, toman, qty } from './fa';

/*
 * Taking goods back — screen ۰۳ of the design.
 *
 * Three things have to move together or the shop's books stop agreeing with
 * itself: the money goes out, the stock comes back, and the tax authority is
 * told. The design's flow makes each an explicit choice rather than an
 * assumption, and that is right — a refund for a spoiled item must NOT restock,
 * and silently doing so inflates inventory by exactly the amount that was
 * thrown away.
 *
 * The reason is mandatory. It is what a manager reviews at the end of the week,
 * and "مرجوعی" with no cause is the row that starts an argument nobody can
 * settle.
 */

interface LookupLine {
  name: string;
  quantity: number;
  unitPriceRial: number;
  lineTotalRial: number;
}

interface Lookup {
  id: string;
  number: string;
  issuedAt: string;
  cashierName: string;
  status: string;
  refundable: boolean;
  totalRial: number;
  lines: LookupLine[];
  payments: { method: string; amountRial: number; isRefund: boolean }[];
}

const REASONS = ['کالای معیوب', 'تغییر نظر مشتری', 'سایز نامناسب', 'اشتباه صندوق'];

const METHOD_LABEL: Record<string, string> = {
  cash: 'نقدی', card: 'کارت‌خوان', transfer: 'کارت به کارت',
  credit: 'نسیه', voucher: 'کارت هدیه', online: 'آنلاین',
};

export function ReturnsScreen({
  canRefund, onDone, onBack,
}: {
  canRefund: boolean;
  onDone: (message: string) => void;
  onBack: () => void;
}) {
  const [number, setNumber] = useState('');
  const [found, setFound] = useState<Lookup | null>(null);
  const [picked, setPicked] = useState<Record<string, number>>({});
  const [reason, setReason] = useState<string | null>(null);
  const [restock, setRestock] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const lookup = useMutation({
    mutationFn: () => api.get<Lookup>(`/till/invoices?number=${encodeURIComponent(toLatin(number))}`),
    onSuccess: (res) => { setFound(res); setPicked({}); setError(null); },
    onError: (e: ServerError) => { setFound(null); setError(e.detail ?? e.message); },
  });

  const refund = useMutation({
    mutationFn: () =>
      api.post<{ message: string }>(`/invoices/${found!.id}/refund`, {
        lineNames: Object.entries(picked).filter(([, n]) => n > 0).map(([name]) => name),
        reason,
        restock,
      }),
    onSuccess: (res) => onDone(res.message),
    onError: (e: ServerError) => setError(e.detail ?? e.message),
  });

  const refundRial = found
    ? found.lines.reduce((a, l) => a + (picked[l.name] ?? 0) * l.unitPriceRial, 0)
    : 0;
  const ready = refundRial > 0 && reason !== null && canRefund;

  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <div style={{ flex: 1, minWidth: 0, padding: 18, display: 'flex', flexDirection: 'column', gap: 14, overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={onBack}
            style={{
              width: 44, height: 44, borderRadius: 10, border: '1px solid var(--luma-line-strong)',
              background: 'var(--luma-card)', fontSize: 16,
            }}
          >
            →
          </button>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: 'var(--luma-placeholder)' }}>
              ۰۳ · مرجوعی
            </div>
            <div style={{ fontWeight: 800, fontSize: 24 }}>فاکتور را پیدا کنید</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <div
            style={{
              flex: 1, height: 56, background: 'var(--luma-card)', border: '1px solid var(--luma-line-strong)',
              borderRadius: 12, display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px',
              boxShadow: '0 0 0 4px rgba(249,124,21,.10)',
            }}
          >
            <span style={{ fontSize: 18, color: 'var(--luma-placeholder)' }}>⌕</span>
            <input
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && number.trim()) lookup.mutate(); }}
              placeholder="شماره فاکتور روی رسید"
              autoFocus
              className="pos-num"
              style={{ flex: 1, border: 0, background: 'transparent', fontSize: 17, fontWeight: 600 }}
            />
          </div>
          <button
            onClick={() => lookup.mutate()}
            disabled={!number.trim() || lookup.isPending}
            style={{
              width: 150, height: 56, borderRadius: 12, background: 'var(--luma-accent)',
              color: '#FFF', fontWeight: 700, fontSize: 14.5,
            }}
          >
            {lookup.isPending ? 'جست‌وجو…' : 'پیدا کن'}
          </button>
        </div>

        {error && !found && (
          <div
            style={{
              background: 'var(--luma-tint-danger-bg)', border: '1px solid var(--luma-tint-danger-border)',
              borderRadius: 12, padding: '12px 14px', fontSize: 13.5,
              color: 'var(--luma-tint-danger-text)', fontWeight: 600,
            }}
          >
            {error}
          </div>
        )}

        {found && (
          <>
            <div style={{ background: 'var(--luma-card)', border: '1px solid var(--luma-line)', borderRadius: 12, overflow: 'hidden' }}>
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                  borderBottom: '1px solid var(--pos-rule)', background: 'var(--luma-sunken)',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div className="pos-num" style={{ fontWeight: 800, fontSize: 15 }}>
                    فاکتور {fa(found.number)}
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--luma-muted)' }}>
                    صندوق‌دار: {found.cashierName} ·{' '}
                    {found.payments.map((p) => METHOD_LABEL[p.method] ?? p.method).join('، ')} ·{' '}
                    {toman(found.totalRial)}
                  </div>
                </div>
                {!found.refundable && (
                  <div
                    style={{
                      fontSize: 11.5, fontWeight: 700, background: 'var(--luma-tint-danger-bg)',
                      color: 'var(--luma-tint-danger-text)', border: '1px solid var(--luma-tint-danger-border)',
                      borderRadius: 7, padding: '3px 9px',
                    }}
                  >
                    {found.status === 'refunded' ? 'قبلاً مرجوع شده' : 'ابطال‌شده'}
                  </div>
                )}
              </div>

              {found.lines.map((l) => {
                const n = picked[l.name] ?? 0;
                return (
                  <div
                    key={l.name}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                      borderBottom: '1px solid var(--pos-rule-faint)',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{l.name}</div>
                      <div className="pos-num" style={{ fontSize: 12, color: 'var(--luma-muted)' }}>
                        خریداری‌شده {qty(l.quantity)} · {toman(l.unitPriceRial)}
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4, background: 'var(--luma-surface)',
                        border: '1px solid var(--pos-rule-strong)', borderRadius: 10, padding: 2,
                      }}
                    >
                      <button
                        onClick={() => setPicked((p) => ({ ...p, [l.name]: Math.max(0, n - 1) }))}
                        disabled={!found.refundable}
                        style={{ width: 44, height: 44, borderRadius: 8, fontSize: 19, fontWeight: 700 }}
                      >
                        −
                      </button>
                      <div className="pos-num" style={{ minWidth: 38, textAlign: 'center', fontWeight: 700, fontSize: 15 }}>
                        {fa(n)}
                      </div>
                      {/* Capped at what was actually bought: refunding three of
                          two is how a till becomes a cash dispenser. */}
                      <button
                        onClick={() => setPicked((p) => ({ ...p, [l.name]: Math.min(l.quantity, n + 1) }))}
                        disabled={!found.refundable}
                        style={{ width: 44, height: 44, borderRadius: 8, fontSize: 19, fontWeight: 700 }}
                      >
                        +
                      </button>
                    </div>
                    <div className="pos-num" style={{ width: 130, textAlign: 'left', fontWeight: 700, fontSize: 15 }}>
                      {toman(n * l.unitPriceRial)}
                    </div>
                  </div>
                );
              })}
            </div>

            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--luma-ink-2)', marginBottom: 8 }}>
                دلیل مرجوعی
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {REASONS.map((r) => {
                  const active = reason === r;
                  return (
                    <button
                      key={r}
                      onClick={() => setReason(r)}
                      style={{
                        height: 44, padding: '0 16px', borderRadius: 10, fontWeight: 700, fontSize: 13.5,
                        border: `1px solid ${active ? 'var(--luma-accent)' : 'var(--luma-line-strong)'}`,
                        background: active ? 'var(--luma-accent-tint)' : 'var(--luma-card)',
                        color: active ? 'var(--luma-accent-tint-text)' : undefined,
                      }}
                    >
                      {r}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      <div
        style={{
          width: 428, flex: 'none', background: 'var(--luma-card)',
          borderInlineStart: '1px solid var(--luma-line)', padding: 18,
          display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: 'var(--luma-placeholder)' }}>
          خلاصه بازگشت
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--luma-ink-2)' }}>مبلغ قابل بازگشت</span>
          <span className="pos-num" style={{ fontWeight: 800, fontSize: 32, lineHeight: 1 }}>{toman(refundRial)}</span>
        </div>

        <div style={{ height: 1, background: 'var(--pos-rule)' }} />

        {/*
          Restocking is an explicit choice, not a default that happens quietly.
          A refund for a spoiled item that restocks inflates inventory by exactly
          the amount that went in the bin.
        */}
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--luma-ink-2)' }}>کالا به موجودی برگردد؟</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { on: true, label: 'بله — سالم است' },
            { on: false, label: 'خیر — معیوب' },
          ].map((o) => (
            <button
              key={String(o.on)}
              onClick={() => setRestock(o.on)}
              style={{
                flex: 1, height: 52, borderRadius: 10, fontWeight: 700, fontSize: 14,
                border: `1px solid ${restock === o.on ? 'var(--luma-accent)' : 'var(--luma-line-strong)'}`,
                background: restock === o.on ? 'var(--luma-accent-tint)' : 'var(--luma-card)',
                color: restock === o.on ? 'var(--luma-accent-tint-text)' : undefined,
              }}
            >
              {o.label}
            </button>
          ))}
        </div>

        <div
          style={{
            background: 'var(--luma-sunken)', border: '1px solid var(--pos-rule-strong)',
            borderRadius: 12, padding: '12px 14px', fontSize: 12.5,
            color: 'var(--pos-ink-3)', lineHeight: 1.65,
          }}
        >
          {restock ? 'کالاها به موجودی شعبه برمی‌گردند و ' : 'کالاها به موجودی برنمی‌گردند و '}
          مرجوعی با نام شما در گزارش ممیزی ثبت می‌شود.
        </div>

        {error && found && (
          <div
            style={{
              background: 'var(--luma-tint-danger-bg)', border: '1px solid var(--luma-tint-danger-border)',
              borderRadius: 10, padding: '10px 14px', fontSize: 13.5,
              color: 'var(--luma-tint-danger-text)', fontWeight: 600,
            }}
          >
            {error}
          </div>
        )}

        {!canRefund && (
          <div
            style={{
              background: 'var(--luma-tint-warning-bg)', border: '1px solid var(--luma-tint-warning-border)',
              borderRadius: 12, padding: '12px 14px', fontSize: 12.5,
              color: 'var(--luma-accent-tint-text)', lineHeight: 1.6,
            }}
          >
            ثبت مرجوعی دسترسی سرشیفت می‌خواهد.
          </div>
        )}

        <div style={{ flex: 1 }} />

        <button
          onClick={() => refund.mutate()}
          disabled={!ready || refund.isPending}
          style={{
            height: 60, borderRadius: 12,
            background: ready && !refund.isPending ? 'var(--luma-accent)' : 'var(--pos-disabled)',
            color: '#FFF', fontWeight: 800, fontSize: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px',
          }}
        >
          <span>
            {refund.isPending ? 'در حال ثبت…'
              : reason === null && refundRial > 0 ? 'دلیل مرجوعی را انتخاب کنید'
              : 'ثبت مرجوعی'}
          </span>
          <span style={{ fontSize: 18 }}>←</span>
        </button>
      </div>
    </div>
  );
}
